import React, { useContext, useEffect, useState } from "react";
import Select from "react-select";
import DropdownMultiSelect from "./DropdownMultiSelect";
import { useLocation } from "react-router-dom";
import { getEntities } from "../API";
import { ReportParamsContext } from "../context/ReportParamsContext";
import { PROVINCES } from "../Constants";


const ROUTES_WITHOUT_DATES = ["monthly_merchandising", "banner_by_month"];
const ROUTES_WITHOUT_MANUFACTURER = ["call_completion", "distribution_drop", "photo_capture", "call_time", "task_report", "visit_report"];

function normalizeResponse(response) {
  if (Array.isArray(response)) return response;
  if (response?._embedded) return Object.values(response._embedded).flat();
  if (response?.content) return response.content;
  return [];
}

export default function ReportParametersSelector() {
  const [commonParams, setCommonParams] = useContext(ReportParamsContext);
  const { pathname } = useLocation();
  const datesDisabled = ROUTES_WITHOUT_DATES.some((r) => pathname.endsWith(r));
  const manufacturerHidden = ROUTES_WITHOUT_MANUFACTURER.some((r) => pathname.endsWith(r));
  const [manufacturers, setManufacturers] = useState([]);
  const [allTerritories, setAllTerritories] = useState([]);
  const [allRepresentatives, setAllRepresentatives] = useState([]);
  const [filteredTerritories, setFilteredTerritories] = useState([]);
  const [filteredRepresentatives, setFilteredRepresentatives] = useState([]);

  useEffect(() => {
    getEntities("manufacturers", "?search=status:ACTIVE&size=1000")
      .then((data) => setManufacturers(normalizeResponse(data)));

    getEntities("territories", "?size=1000").then((data) => {
      const all = normalizeResponse(data);
      setAllTerritories(all);
      // Restore filtered list if provinces already selected in context
      const provinces = commonParams.provinces ?? [];
      setFilteredTerritories(
        provinces.length > 0
          ? all.filter((t) => provinces.find((p) => p.id == t.province?.id))
          : []
      );
    });

    getEntities("representatives", "?size=1000").then((data) => {
      const all = normalizeResponse(data);
      setAllRepresentatives(all);
      // Restore filtered list if territories already selected in context
      const territories = commonParams.territories ?? [];
      setFilteredRepresentatives(
        territories.length > 0
          ? all.filter((r) =>
              territories.find((t) => r.territories?.find((t2) => t2.id == t.id))
            )
          : []
      );
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleProvinceSelection = (selected) => {
    const ft =
      selected.length > 0
        ? allTerritories.filter((t) => selected.find((p) => p.id == t.province?.id))
        : [];
    setFilteredTerritories(ft);
    setFilteredRepresentatives([]);
    setCommonParams((prev) => ({
      ...prev,
      provinces: selected,
      territories: [],
      representatives: [],
    }));
  };

  const handleTerritorySelection = (selected) => {
    const fr =
      selected.length > 0
        ? allRepresentatives.filter((r) =>
            selected.find((t) => r.territories?.find((t2) => t2.id == t.id))
          )
        : [];
    setFilteredRepresentatives(fr);
    setCommonParams((prev) => ({
      ...prev,
      territories: selected,
      representatives: [],
    }));
  };

  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className="border-bottom bg-light">
      <div className="d-flex justify-content-end px-4 pt-1 pb-1">
        <button
          className="btn btn-sm btn-link text-muted text-decoration-none p-0"
          onClick={() => setCollapsed((c) => !c)}
        >
          {collapsed ? "▼ Show parameters" : "▲ Hide parameters"}
        </button>
      </div>

      {!collapsed && (
        <div className="px-4 pb-3">
          <div className="row g-2 align-items-end">
            {!manufacturerHidden && (
              <div className="col-md-3">
                <label className="form-label small mb-1">Manufacturer</label>
                <Select
                  options={manufacturers}
                  getOptionLabel={(o) => o.name}
                  getOptionValue={(o) => o.id}
                  value={commonParams.manufacturer ?? null}
                  onChange={(v) =>
                    setCommonParams((prev) => ({ ...prev, manufacturer: v }))
                  }
                  placeholder="Select manufacturer..."
                  isClearable
                />
              </div>
            )}
            <div className="col-md-2">
              <label className="form-label small mb-1">
                From{datesDisabled && <span className="text-muted ms-1" style={{ fontSize: "0.7rem" }}>(not used)</span>}
              </label>
              <input
                type="date"
                className="form-control"
                value={commonParams.fromDate ?? ""}
                disabled={datesDisabled}
                onChange={(e) =>
                  setCommonParams((prev) => ({ ...prev, fromDate: e.target.value }))
                }
              />
            </div>
            <div className="col-md-2">
              <label className="form-label small mb-1">
                To{datesDisabled && <span className="text-muted ms-1" style={{ fontSize: "0.7rem" }}>(not used)</span>}
              </label>
              <input
                type="date"
                className="form-control"
                value={commonParams.toDate ?? ""}
                disabled={datesDisabled}
                onChange={(e) =>
                  setCommonParams((prev) => ({ ...prev, toDate: e.target.value }))
                }
              />
            </div>
            <div className="col-md-3">
              <label className="form-label small mb-1">Provinces</label>
              <DropdownMultiSelect
                options={PROVINCES}
                getOptionLabel={(o) => o.name}
                getOptionValue={(o) => o.id}
                value={commonParams.provinces ?? []}
                onChange={handleProvinceSelection}
                placeholder="Select provinces..."
              />
            </div>
          </div>
          <div className="row g-2 mt-1 align-items-end">
            <div className="col-md-6">
              <label className="form-label small mb-1">Territories</label>
              <DropdownMultiSelect
                options={filteredTerritories}
                value={commonParams.territories ?? []}
                onChange={handleTerritorySelection}
                getOptionLabel={(o) => o.name}
                getOptionValue={(o) => o.id}
                placeholder="Select territories..."
                disabled={filteredTerritories.length === 0}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label small mb-1">Representatives</label>
              <DropdownMultiSelect
                options={filteredRepresentatives}
                value={commonParams.representatives ?? []}
                onChange={(v) => setCommonParams((prev) => ({ ...prev, representatives: v }))}
                getOptionLabel={(o) => `${o.firstName} ${o.lastName}`}
                getOptionValue={(o) => o.id}
                placeholder="Select representatives..."
                disabled={filteredRepresentatives.length === 0}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
